import { useLocation, useMatch, useNavigate } from "react-router"

import { useAgentState } from "@/agent-state"
import { useDisplayAgentStatus } from "@/agent-state/agent-status-hooks"
import { isInManualCall } from "@/agent-state/state-helpers"
import { useAnalytics, userEventsMap } from "@/components/core/Root/analytics"
import { routesConfig } from "@/config/routes"
import { useLogger } from "@/hooks/useLogger"

import { useACWButtonsHook } from "../CallPanel/ACWButtons/hook"
import { getSnapshot } from "../core/ConnectCCP/contact_helpers"
import { useModal } from "../core/Root/modal-root"
import { DialogGoOfflineWithContact } from "./dialog-go-offline-with-contact"
import { useGoOfflineHook } from "./go-offline-with-contact"

export function useAgentStatusHook() {
  const location = useLocation()
  const matchStudentDetailsPage = useMatch(
    "/campaigns/:campaignId/students/:studentId",
  )
  const navigate = useNavigate()
  const track = useTrackAgentStatusChange()
  const log = useLogger()
  const { showModal } = useModal()
  const { agentRef, send, state } = useAgentState()
  const { setAgentStatus } = useDisplayAgentStatus()
  const { goOffline } = useGoOfflineHook(
    state.context.taskData?.caseData || null,
  )

  const acwButtonState = useACWButtonsHook({
    currentCase: state.context.taskData?.caseData || null,
    isInboundCall: state.context.taskMeta?.callDirection === "inbound",
  })

  const agent = agentRef.current
  const currentContactId = state.context.taskMeta?.contactId
  const pseudoACW = state.matches({ busy: { after_call: "pseudo_acw" } })

  // disable condition:
  // - agent is offline
  // - agent has a contact(connected or connecting) or pseudoACW(not connected)
  // - in manual calling panel(student details page)
  const disabledInManualCalling =
    !!matchStudentDetailsPage &&
    isInManualCall(state) &&
    (!!currentContactId || pseudoACW)

  const onChange = async (status: "offline" | "online") => {
    try {
      if (status === "offline") {
        if (!state.matches("busy")) {
          await setAgentStatus(status)
          send({ type: "GO_OFFLINE" })

          return
        }
        const goOfflineAction = state.context.historyItem
          ? async () => acwButtonState.onConfirmAndGoOffline(true)
          : goOffline
        await showModal((close) => (
          <DialogGoOfflineWithContact
            callReportSaved={!!state.context.historyItem}
            onClose={close}
            onConfirm={goOfflineAction}
          />
        ))
      } else {
        if (!(await setAgentStatus(status))) return
        send({ type: "GO_ONLINE" })

        // Redirect to the call panel when agent goes online outside of the call panel
        const shouldRedirectToCallPanel =
          location.pathname !== routesConfig.AUTOMATIC_CALL_PANEL &&
          status === "online"

        if (shouldRedirectToCallPanel) {
          navigate(routesConfig.AUTOMATIC_CALL_PANEL, { replace: true })
        }
      }
    } catch (error) {
      log.error(error, { agent: getSnapshot(agent) })
    }
    track(status)
  }

  return {
    isDisabled: disabledInManualCalling,
    onChange,
  }
}

function useTrackAgentStatusChange() {
  const { trackEvent } = useAnalytics()

  return (status: "offline" | "online") => {
    trackEvent(
      status === "online" ? userEventsMap.online : userEventsMap.offline,
    )
  }
}
